import classnames from "classnames";
import {PropsWithChildren, useState} from "react";
import {FaCheck} from "react-icons/fa6";

import {Switch} from "@/components/switch/Switch.tsx";
import {thousandSeparatedInt} from "@/lib/format.tsx";
import {BillingPeriod} from "@/lib/types/billing.ts";
import {PackSelect} from "@/pages/billing/v2/PackSelect.tsx";
import {SubscriptionPack, SubscriptionPackTier} from "@/pages/billing/v2/types.ts";

import styles from "./Pack.module.css";

export interface PackProps extends Omit<SubscriptionPack, "pack_id">, PropsWithChildren {
  billingPeriod: BillingPeriod;
  onChange?: (value: {value: SubscriptionPackTier; active: boolean}) => void;
}

export const Pack = ({title, billingPeriod, tiers, lines, onChange, default_values, children}: PackProps) => {
  const defaultValue = tiers.find((v: SubscriptionPackTier) => v.tier_id === default_values.tier_id)!;
  const [value, setValue] = useState<SubscriptionPackTier>(defaultValue);
  const [active, setActive] = useState<boolean>(!!default_values?.is_active);
  return (
    <div className={classnames(styles.plan, "rounded-4 p-4 h-100", {[styles.active]: active})}>
      <div className="d-flex">
        <h2 className="pulseTextH2Bold">{title}</h2>
        {!default_values.is_mandatory && (
          <div className="ms-auto">
            <Switch
              id={title}
              checked={active}
              disabled={default_values.is_forced}
              onChange={() => {
                setActive(!active);
                if (onChange) {
                  onChange({value, active: !active});
                }
              }}
            />
          </div>
        )}
      </div>
      <div className="mt-3">
        <span className="fs-7 text-uppercase pulse-color-gray-600">
          {value.is_current ? (value.is_custom_limits ? "Current custom plan" : "Current plan") : "Starting at"}
        </span>
      </div>
      <div>
        <span className="fs-5 fw-medium d-flex align-items-center">
          ${" "}
          <span className="pulseTextH2Bold fs-3 me-1">
            {thousandSeparatedInt(billingPeriod === BillingPeriod.MONTH ? value.price_monthly : value.price_annual)}
          </span>
          / mo
        </span>
      </div>
      <div className="mt-4">
        <h3 className="pulseTextH3Bold">Quantity</h3>
        <PackSelect
          className="mt-3"
          options={tiers}
          defaultValue={defaultValue}
          value={value}
          onChange={(selected) => {
            if (selected) {
              setValue(selected);
              setActive(true);
              if (onChange) {
                onChange({value: selected, active: true});
              }
            }
          }}
        />
        <ul className="mt-3 list-unstyled lh-lg">
          {lines.map((line, i) => (
            <li key={i}>
              <FaCheck className="me-2 text-success" />
              <span className="pulseTextBodySmall">{line}</span>
            </li>
          ))}
        </ul>
        {children}
      </div>
    </div>
  );
};
